/**
 * Analytics Wrapper
 * Centralized analytics tracking for Google Analytics 4, Facebook Pixel, etc.
 */

import { getGa4ConfigParams, getGa4EventParams } from './attribution';

/**
 * Initialize Google Analytics 4
 * Add your GA4 measurement ID here
 */
export const GA4_MEASUREMENT_ID = 'G-CZM4X34F9R';

const normalizeAnalyticsPath = (path = '') => {
  if (path === '/app' || path === '/app/') return '/app.html';
  if (path === '/about' || path === '/about/') return '/about.html';
  if (path === '/terms-of-service' || path === '/terms-of-service/') return '/terms-of-service.html';
  return path;
};

/**
 * Initialize Google Analytics
 */
export const initGA4 = (measurementId = GA4_MEASUREMENT_ID) => {
  if (typeof window === 'undefined') return;
  
  // Check if gtag already exists
  if (window.gtag) {
    console.log('✅ Google Analytics already initialized');
    return;
  }
  
  // Create gtag function
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  
  gtag('js', new Date());
  gtag('config', measurementId, {
    send_page_view: false,
    ...getGa4ConfigParams()
  });
  
  // Load GA4 script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);
  
  console.log('✅ Google Analytics 4 initialized');
};

/**
 * Track page view
 */
export const trackPageView = (pagePath = window.location.pathname, pageTitle = document.title) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  const normalizedPath = normalizeAnalyticsPath(pagePath);
  if (window.__SCP_INITIAL_APP_PAGE_VIEW_SENT && normalizedPath === '/app.html') {
    window.__SCP_INITIAL_APP_PAGE_VIEW_SENT = false;
    return;
  }
  const pageLocation = `${window.location.origin}${normalizedPath}${window.location.search || ''}${window.location.hash || ''}`;
  
  window.gtag('event', 'page_view', {
    page_path: normalizedPath,
    page_title: pageTitle,
    page_location: pageLocation,
    ...getGa4EventParams()
  });
  
  console.log('📊 Page view tracked:', normalizedPath);
};

/**
 * Track calculator usage
 */
export const trackCalculatorRun = (calculatorType, inputs = {}) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('event', 'calculator_run', {
    calculator_type: calculatorType,
    user_age: inputs.age,
    user_balance: inputs.balance,
    is_couple: inputs.isCouple,
    withdrawal_strategy: inputs.strategy,
    period_length: inputs.periodLength
  });
  
  console.log('🧮 Calculator run tracked:', calculatorType);
};

/**
 * Track calculator result viewed
 */
export const trackCalculatorResult = (calculatorType, result = {}) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('event', 'calculator_result_viewed', {
    calculator_type: calculatorType,
    sustainable_income: result.sustainableIncome,
    final_balance: result.finalBalance,
    success_rate: result.successRate
  });
};

/**
 * Track user conversion (free to paid)
 */
export const trackConversion = (conversionType = 'signup', value = 0) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('event', 'conversion', {
    conversion_type: conversionType,
    value: value,
    currency: 'AUD'
  });
  
  console.log('💰 Conversion tracked:', conversionType, value);
};

/**
 * Track button clicks
 */
export const trackButtonClick = (buttonName, location = '') => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('event', 'button_click', {
    button_name: buttonName,
    button_location: location
  });
};

/**
 * Track form submissions
 */
export const trackFormSubmit = (formName, success = true) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('event', 'form_submit', {
    form_name: formName,
    success: success
  });
};

/**
 * Track outbound links
 */
export const trackOutboundLink = (url, linkText = '') => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('event', 'click', {
    event_category: 'outbound',
    event_label: url,
    link_text: linkText
  });
};

/**
 * Track file downloads
 */
export const trackDownload = (fileName, fileType = '') => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('event', 'file_download', {
    file_name: fileName,
    file_type: fileType
  });
};

/**
 * Track errors
 */
export const trackError = (errorMessage, errorLocation = '') => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('event', 'exception', {
    description: errorMessage,
    fatal: false,
    location: errorLocation
  });
};

/**
 * Track user engagement time
 */
export const trackEngagement = (engagementTime) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('event', 'user_engagement', {
    engagement_time_msec: engagementTime
  });
};

/**
 * Set user properties (for segmentation)
 */
export const setUserProperties = (properties = {}) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('set', 'user_properties', properties);
};

/**
 * Track scroll depth
 */
export const initScrollTracking = () => {
  if (typeof window === 'undefined') return;
  
  let scrollDepths = [25, 50, 75, 100];
  let trackedDepths = new Set();
  
  const trackScroll = () => {
    const scrollPercentage = Math.round(
      (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
    );
    
    scrollDepths.forEach(depth => {
      if (scrollPercentage >= depth && !trackedDepths.has(depth)) {
        trackedDepths.add(depth);
        
        if (window.gtag) {
          window.gtag('event', 'scroll', {
            percent_scrolled: depth
          });
        }
        
        console.log(`📜 Scroll depth tracked: ${depth}%`);
      }
    });
  };
  
  window.addEventListener('scroll', trackScroll, { passive: true });
};

/**
 * Initialize all analytics
 */
export const initAnalytics = (measurementId = GA4_MEASUREMENT_ID) => {
  if (typeof window === 'undefined') return;
  
  // Initialize GA4
  initGA4(measurementId);
  
  // Track initial page view
  setTimeout(() => {
    trackPageView();
  }, 100);
  
  // Initialize scroll tracking
  initScrollTracking();
  
  // Track engagement time
  let startTime = Date.now();
  window.addEventListener('beforeunload', () => {
    const engagementTime = Date.now() - startTime;
    trackEngagement(engagementTime);
  });
  
  console.log('✅ Analytics fully initialized');
};

/**
 * Disable analytics (for privacy/GDPR compliance)
 */
export const disableAnalytics = () => {
  if (typeof window === 'undefined') return;
  
  window[`ga-disable-${GA4_MEASUREMENT_ID}`] = true;
  console.log('🔒 Analytics disabled');
};

/**
 * Enable analytics (after user consent)
 */
export const enableAnalytics = () => {
  if (typeof window === 'undefined') return;
  
  window[`ga-disable-${GA4_MEASUREMENT_ID}`] = false;
  initAnalytics();
  console.log('✅ Analytics enabled');
};

export default {
  initGA4,
  initAnalytics,
  trackPageView,
  trackCalculatorRun,
  trackCalculatorResult,
  trackConversion,
  trackButtonClick,
  trackFormSubmit,
  trackOutboundLink,
  trackDownload,
  trackError,
  trackEngagement,
  setUserProperties,
  initScrollTracking,
  disableAnalytics,
  enableAnalytics
};

